import React, { useEffect, useMemo, useState } from 'react';
import { Typography, theme } from 'antd';
import { motion } from 'framer-motion';
import { IconUsers, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { INearbyOperator } from '../interfaces/heavyDutyMachinery/IService';
import { getNearbyOperators } from '../services/HeavyDutyMachineryService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { SURFACE_1_SOFT, TEAL } from '../theme/creamyTokens';
import NearbyOperatorsSheet from './NearbyOperatorsSheet';

const { Text } = Typography;

export interface INearbyOperatorsSectionProps {
  spaceId: string;
  /**
   * The JOB's coordinates -- the delivery point when the guest asked for
   * delivery. Left out, the server measures from the machine's own
   * location, which is right for a guest collecting it themselves and
   * wrong for everybody else, so pass them whenever a booking has them
   * (Booking.deliveryLatitude/deliveryLongitude).
   */
  lat?: number;
  lng?: number;
  /** The rental's own dates, so a busy operator is shown as busy rather than as a wasted tap. Naive space-local strings, same as everywhere else in checkout. */
  startAt?: string;
  endAt?: string;
  /** See INearbyOperatorsSheetProps.mode -- 'browse' before the rental is secured, 'hire' after. */
  mode: 'browse' | 'hire';
  /** Called with the chosen operator in 'hire' mode; the caller decides where that leads. */
  onHire?: (operator: INearbyOperator) => void;
  style?: React.CSSProperties;
}

/**
 * "There are operators working near this job" -- one strip that opens the
 * full list (see NearbyOperatorsSheet).
 *
 * This is the whole feature in one component on purpose: it fetches, it
 * decides whether there is anything worth showing, it renders the strip and
 * it owns the sheet. The three places it appears -- checkout, the
 * just-paid screen and the booking's own page -- each mount it with one
 * line and differ only in `mode`, rather than each growing their own copy
 * of the same fetch, the same empty check and the same sheet.
 *
 * Renders NOTHING when the list comes back empty, which is the normal
 * answer in three different cases the caller never has to tell apart: the
 * host does not allow outside operators on this machine, nobody compatible
 * is within range, or the Services feature is off. Same "empty is the cue
 * to skip the section" convention as the accessory and insurance steps.
 */
const NearbyOperatorsSection: React.FC<INearbyOperatorsSectionProps> = ({
  spaceId,
  lat,
  lng,
  startAt,
  endAt,
  mode,
  onHire,
  style,
}) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const [operators, setOperators] = useState<INearbyOperator[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getNearbyOperators(spaceId, { startAt, endAt, lat, lng })
      .then((found) => {
        if (!cancelled) setOperators(found);
      })
      // A failure here is never worth a message: this section is an extra,
      // and a guest who came to rent a machine should not be handed an
      // error about operators they did not ask for. Empty behaves exactly
      // like "there are none", which is what an outage effectively means.
      .catch(() => {
        if (!cancelled) setOperators([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [spaceId, startAt, endAt, lat, lng]);

  // The cheapest way in, which is what a strip like this is for -- the
  // detail lives one tap away. Hourly and fixed-job prices are deliberately
  // compared as plain numbers: they are not the same unit, but "from" only
  // ever promises the smallest figure on the list, and every row states its
  // own unit next to its own price.
  const cheapest = useMemo(() => {
    const prices = operators
      .map((o) => (o.pricingMode === 'FixedJob' ? o.fixedJobPrice : o.hourlyRate))
      .filter((p): p is number => p != null);
    return prices.length > 0 ? Math.min(...prices) : undefined;
  }, [operators]);

  const currencyCode = operators[0]?.hostCurrencyCode ?? 'EUR';

  if (loading || operators.length === 0) return null;

  return (
    <>
      <motion.div
        whileTap={{ scale: 0.98 }}
        role="button"
        tabIndex={0}
        onClick={() => setSheetOpen(true)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setSheetOpen(true);
        }}
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          cursor: 'pointer',
          background: token.colorBgContainer,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 16,
          padding: 14,
          ...style,
        }}
      >
        <span
          style={{
            display: 'flex',
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            background: SURFACE_1_SOFT,
            color: TEAL,
          }}
        >
          <IconUsers size={20} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text strong style={{ display: 'block', fontSize: 14 }}>
            {operators.length === 1
              ? translate(t, TRANSLATIONS.NEARBY_OPERATORS_TRIGGER_TITLE_SINGULAR)
              : translate(t, TRANSLATIONS.NEARBY_OPERATORS_TRIGGER_TITLE, { count: operators.length })}
          </Text>
          {cheapest != null && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.NEARBY_OPERATORS_TRIGGER_FROM, {
                price: formatCurrency(cheapest, currencyCode, i18n.language),
              })}
            </Text>
          )}
        </div>
        <span style={{ display: 'flex', flexShrink: 0, color: token.colorTextTertiary }}>
          <IconChevronRight size={18} />
        </span>
      </motion.div>

      <NearbyOperatorsSheet
        open={sheetOpen}
        operators={operators}
        loading={false}
        mode={mode}
        onHire={onHire}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
};

export default NearbyOperatorsSection;
